import React from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Filter, X } from "lucide-react"

import {
  ANY_RESULT,
  decodeTraceValue,
  encodeTraceValue,
} from "../recentMetricValues"

const FLOAT_OPERATORS = [">", "<", "=", ">=", "<="] as const

type FloatOperator = (typeof FLOAT_OPERATORS)[number]

interface MetricValueFilterProps {
  isFloatMetric: boolean
  isValuesMetric?: boolean
  isTraceMetric?: boolean
  filterValue: string
  filterOperator: FloatOperator
  /** Unique sorted string options derived from displayed data (string metrics only) */
  stringOptions: string[]
  onFilterValueChange: (value: string) => void
  onFilterOperatorChange: (op: FloatOperator) => void
}

export const MetricValueFilter: React.FC<MetricValueFilterProps> = ({
  isFloatMetric,
  isValuesMetric = false,
  isTraceMetric = false,
  filterValue,
  filterOperator,
  stringOptions,
  onFilterValueChange,
  onFilterOperatorChange,
}) => {
  const isAnyResult = filterValue === ANY_RESULT
  const selectedTraceValue = isTraceMetric
    ? decodeTraceValue(filterValue)
    : null
  const traceOptions =
    selectedTraceValue !== null && !stringOptions.includes(selectedTraceValue)
      ? [...stringOptions, selectedTraceValue]
      : stringOptions

  return (
    <div className="px-4 py-2 border-b border-coval-border bg-surface-0">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-coval-text-muted shrink-0" />
        <span className="text-xs text-coval-text-muted shrink-0">
          Metric Value
        </span>
        {isFloatMetric ? (
          <>
            {!isAnyResult && (
              <>
                <Select
                  value={filterValue ? filterOperator : ""}
                  onValueChange={(val) => {
                    if (val) {
                      onFilterOperatorChange(val as FloatOperator)
                    }
                  }}
                >
                  <SelectTrigger
                    className="w-20 shrink-0 h-7"
                    aria-label="Operator"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FLOAT_OPERATORS.map((op) => (
                      <SelectItem key={op} value={op}>
                        {op}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="value"
                  type="number"
                  step="any"
                  value={selectedTraceValue === null ? filterValue : ""}
                  onChange={(e) => onFilterValueChange(e.target.value)}
                  className="h-7"
                  aria-label="Filter value"
                />
              </>
            )}
            <button
              onClick={() => onFilterValueChange(isAnyResult ? "" : ANY_RESULT)}
              className={`text-xs px-2 py-1 rounded border transition-colors shrink-0 ${
                isAnyResult
                  ? "border-coval-border-active bg-coval-contrast/10 text-coval-contrast"
                  : "border-coval-border text-coval-text-muted hover:border-coval-text-muted"
              }`}
            >
              Any result
            </button>
            {isTraceMetric &&
              (traceOptions.length > 0 || selectedTraceValue !== null) && (
                <Select
                  value={selectedTraceValue === null ? "" : filterValue}
                  onValueChange={(val) => onFilterValueChange(val || "")}
                >
                  <SelectTrigger
                    className="h-7"
                    aria-label="Filter historical value"
                    data-dd-privacy="mask"
                    data-ph-capture="false"
                  >
                    <SelectValue placeholder="Historical values" />
                  </SelectTrigger>
                  <SelectContent>
                    {traceOptions.map((val) => (
                      <SelectItem
                        key={val}
                        value={encodeTraceValue(val)}
                        data-dd-privacy="mask"
                        data-ph-capture="false"
                      >
                        {val === "" ? "(empty string)" : val}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
          </>
        ) : (
          <Select
            value={filterValue || ""}
            onValueChange={(val) => onFilterValueChange(val || "")}
          >
            <SelectTrigger
              className="flex-1 h-7"
              aria-label="Filter value"
              data-dd-privacy={isValuesMetric ? "mask" : undefined}
              data-ph-capture={isValuesMetric ? "false" : undefined}
            >
              <SelectValue placeholder="no filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_RESULT}>Any result</SelectItem>
              {stringOptions.map((val) => (
                <SelectItem
                  key={val}
                  value={isValuesMetric ? encodeTraceValue(val) : val}
                  data-dd-privacy={isValuesMetric ? "mask" : undefined}
                  data-ph-capture={isValuesMetric ? "false" : undefined}
                >
                  {val === "" && isValuesMetric ? "(empty string)" : val}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {filterValue && (
          <Tooltip content="Clear filter">
            <button
              onClick={() => onFilterValueChange("")}
              className="shrink-0 text-coval-text-muted hover:text-coval-text"
              aria-label="Clear filter"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
